/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Col, Row } from "@duxton-web/ui"
import { FC } from "react"
import { formatNumber } from "utils/rating-summary-helper"
import { FeedbackOverview } from "types/api/menuFeedback.type"
import isEmpty from "lodash/isEmpty"
import { useTranslationsMap } from "common/utils/i18n"
import { ServiceType } from "constants/feedback"
import BasicRating from "./BasicRating"
import DetailedRating from "./DetailedRating"

export interface RatingSummaryProps {
  feedbackOverview?: FeedbackOverview
  className?: string
  serviceType?: string
  hasSelfPickup?: boolean
}

export const RatingSummary: FC<RatingSummaryProps> = props => {
  const {
    feedbackOverview,
    className,
    serviceType,
    hasSelfPickup = false,
  } = props
  const ratingCountFormatted = feedbackOverview?.ratingCount
    ? formatNumber(feedbackOverview.ratingCount)
    : 0

  const textMap = useTranslationsMap({
    dineIn: "feedback.filters.serviceType.dineIn",
    delivery: "feedback.filters.serviceType.delivery",
    deliveryAndSelfPickup: "feedback.filters.serviceType.deliveryAndSelfPickup",
    average: "feedback.headings.average",
  })

  const serviceTypeText: { [key: string]: string } = {
    [ServiceType.DELIVERY]: hasSelfPickup
      ? textMap.deliveryAndSelfPickup
      : textMap.delivery,
    [ServiceType.DINEIN]: textMap.dineIn,
    [ServiceType.ALL]: textMap.all,
  }

  return (
    <div
      className={className}
      css={css({
        width: 350,
        padding: 16,
        borderRadius: 10,
        boxShadow: "0px 6px 12px rgba(28, 28, 28, 0.1)",
      })}
    >
      <Row>
        <Col>
          <p
            css={css({
              fontStyle: "normal",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "18px",
              margin: 0,
            })}
          >
            {serviceType ? serviceTypeText[serviceType] : textMap.average}
          </p>
        </Col>
      </Row>
      {feedbackOverview &&
        isEmpty(feedbackOverview?.componentRating?.ratings) && (
          <BasicRating
            ratingCountFormatted={ratingCountFormatted}
            feedbackOverview={feedbackOverview}
          />
        )}
      {feedbackOverview &&
        !isEmpty(feedbackOverview?.componentRating?.ratings) && (
          <DetailedRating
            ratingCountFormatted={ratingCountFormatted}
            feedbackOverview={feedbackOverview}
          />
        )}
    </div>
  )
}
